import React, { useEffect } from 'react';
import styles from '../editModal.module.scss';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import restFactory from 'v2/utils/restFactory';
import { DataProduct } from 'v2/models';
import ConfirmationModal from 'v2/common/ConfirmationModal/ConfirmationModal';
import FormLabel from 'v2/form/FormLabel/FormLabel';
import classnames from 'classnames';
import { TranslationKeys, PostProcessKeys } from 'translations';
import { useTranslation } from 'react-i18next';

interface EditDataProductDescriptionModalProps {
  show: boolean;
  dataProduct: DataProduct;
  onClose: (dataProduct?: DataProduct) => void;
}

export interface DataProductDescriptionField {
  description: string;
}

const EditDataProductDescriptionModal = ({
  show,
  dataProduct,
  onClose,
}: EditDataProductDescriptionModalProps) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    formState: { isValid },
    register,
    control: { _fields },
  } = useForm<DataProductDescriptionField>({
    mode: 'onChange',
    defaultValues: { description: dataProduct?.description },
  });

  const {
    isLoading: isDataProductDescriptionMutateLoading,
    mutate: dataProductDescriptionMutate,
  } = useMutation(
    async (data: DataProductDescriptionField) => {
      return await restFactory.patch<DataProduct>(
        `/dj/api/v1/data_product/${dataProduct?.id}/`,
        data
      );
    },
    {
      onSuccess: (data) => {
        toast.success(
          `${t(TranslationKeys.dataProduct_one, {
            postProcess: PostProcessKeys.TitleCase,
          })} description saved.`
        );
        onClose(data);
      },
      onError: () => {
        toast.error(
          `Error saving ${t(TranslationKeys.dataProduct_one)} description.`
        );
      },
    }
  );

  useEffect(() => {
    if (show) {
      // Focus on the first input item
      _fields?.description?._f.ref.focus?.();
    }
  }, [show]);

  const onSubmit = async (data: DataProductDescriptionField) => {
    if (isValid) {
      dataProductDescriptionMutate(data);
    }
  };

  return (
    <ConfirmationModal
      className={styles.viewModals}
      centered={true}
      animation={true}
      show={show}
      onCancel={() => onClose()}
      okBtnText="Save"
      onOk={handleSubmit(onSubmit)}
      okBtnDisabled={!isValid}
      isLoading={isDataProductDescriptionMutateLoading}
      title="Edit Description"
    >
      <div>
        <div className={classnames(styles.dataProductForm)}>
          <FormLabel
            label={`${t(TranslationKeys.dataProduct_one, {
              postProcess: PostProcessKeys.TitleCase,
            })} Description`}
            required={false}
          />
          <div>
            <textarea
              className={classnames('form-control', styles.textArea)}
              rows={8}
              aria-label="Description input"
              {...register('description')}
            />
          </div>
        </div>
      </div>
    </ConfirmationModal>
  );
};

export default EditDataProductDescriptionModal;
